import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { useProject } from '../../utils/ProjectContext';
import { getImage, setImage } from '../../utils/idbHelpers';
import '../../css/Home.css';
import BottomNavBar from "../navigation/BottomNavBar";

const MenuButton = ({ title, icon, onClick }) => (
    <button className="menu-button" onClick={onClick}>
        <i className={`fas ${icon}`}></i>
        <span>{title}</span>
    </button>
);

const Home = () => {
    const navigate = useNavigate();
    const { projectId: urlProjectId } = useParams();
    const { user } = useAuth();
    const { projectId: contextProjectId, BASE_URL, setProjectData, setProjectName } = useProject();
    const [projects, setProjects] = useState([]);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const token = localStorage.getItem('token');

    const fetchProfileImage = async (projectId, imageName) => {
        try {
            let cachedImage = await getImage(projectId, imageName);
            if (cachedImage) {
                setProfileImageUrl(cachedImage);
            } else {
                const response = await fetch(`${BASE_URL}/project/${projectId}/image/${imageName}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();

                if (data.imageUrl) {
                    const image = new Image();
                    image.onload = async () => {
                        await setImage(projectId, imageName, data.imageUrl);
                        setProfileImageUrl(data.imageUrl);
                    };
                    image.src = data.imageUrl;
                }
            }
        } catch (error) {
            console.error('Error fetching profile image:', error);
        }
    };

    // Effect to handle URL project ID changes
    useEffect(() => {
        if (urlProjectId && urlProjectId !== contextProjectId) {
            handleProjectChange(urlProjectId);
        }
    }, [urlProjectId]);

    // Effect to handle initial project load from localStorage
    useEffect(() => {
        if (!urlProjectId) {
            const lastSelectedProject = localStorage.getItem('selectedProject');
            if (lastSelectedProject) {
                navigate(`/home/${lastSelectedProject}`);
            }
        }
    }, []);

    const handleProjectChange = async (newProjectId) => {
        if (!newProjectId) {
            setProfileImageUrl(null);
            setProjectData(null);
            setProjectName('');
            localStorage.removeItem('selectedProject');
            navigate('/home');
            return;
        }
        
        setIsLoading(true);
        localStorage.setItem('selectedProject', newProjectId);
        navigate(`/home/${newProjectId}`);
        
        try {
            const response = await fetch(`${BASE_URL}/api/projects/${newProjectId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            
            // Update project context
            setProjectData(data.project);
            setProjectName(data.project.name);

            // Handle profile image
            if (data.project.profileImage) {
                const profileImage = data.project.data.images.find(
                    (image) => image._id === data.project.profileImage
                );
                if (profileImage) {
                    await fetchProfileImage(data.project._id, profileImage.name);
                } else {
                    setProfileImageUrl(null);
                }
            } else {
                setProfileImageUrl(null);
            }
        } catch (error) {
            console.error('Error fetching project details:', error);
            setProfileImageUrl(null);
            setProjectData(null);
            setProjectName('');
        }
        
        setIsLoading(false);
    };

    const handleProjectSelect = (event) => {
        handleProjectChange(event.target.value);
    };

    useEffect(() => {
        if (contextProjectId) {
            const fetchInitialProfileImage = async () => {
                try {
                    const response = await fetch(`${BASE_URL}/api/projects/${contextProjectId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                    const project = await response.json();
                    
                    if (project.project.profileImage) {
                        const profileImage = project.project.data.images.find(
                            (image) => image._id === project.project.profileImage
                        );
                        if (profileImage) {
                            await fetchProfileImage(project.project._id, profileImage.name);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching initial project details:', error);
                }
            };
            fetchInitialProfileImage();
        }
    }, [contextProjectId]);

    useEffect(() => {
        if (user) {
            const fetchProjects = async () => {
                try {
                    console.log('Fetching projects from:', `${BASE_URL}/api/projects/all`);
                    const response = await fetch(`${BASE_URL}/api/projects/all`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('Failed to fetch projects:', {
                            status: response.status,
                            statusText: response.statusText,
                            error: errorText
                        });
                        throw new Error(`Failed to fetch projects: ${response.statusText}`);
                    }

                    const data = await response.json();
                    console.log('Projects response:', data);
                    
                    console.log('User data:', user);
                    console.log('User role:', user.role);
                    console.log('User owned projects:', user.user?.ownedProjects);

                    if (user.user?.role === 'admin') {
                        console.log('Setting all projects for admin');
                        setProjects(data);
                    } else {
                        console.log('Filtering projects for user');
                        const ownedProjectIds = user.user.ownedProjects.map((p) => {
                            console.log('Project mapping:', p);
                            return p.project;
                        });
                        console.log('Owned project IDs:', ownedProjectIds);
                        
                        const ownedProjects = data.filter((project) => {
                            console.log('Checking project:', project);
                            const isOwned = ownedProjectIds.includes(project._id);
                            console.log(`Project ${project._id} owned: ${isOwned}`);
                            return isOwned;
                        });
                        console.log('Filtered projects:', ownedProjects);
                        setProjects(ownedProjects);
                    }

                    // If there's a contextProjectId but no project data, fetch it
                    if (contextProjectId) {
                        const projectResponse = await fetch(`${BASE_URL}/api/projects/${contextProjectId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        });
                        const projectData = await projectResponse.json();
                        setProjectData(projectData.project);
                        setProjectName(projectData.project.name);
                    }
                    
                    setIsLoading(false);
                } catch (error) {
                    console.error('Error:', error);
                    setIsLoading(false);
                }
            };
            fetchProjects();
        }
    }, [user, contextProjectId]);

    return (
        <div className="home-container">
            <div className="home-content">
                <div className="header-container">
                    <h1 className="welcome-header">Welcome {user?.user?.name.split(' ')[0] || 'User'}</h1>
                    <div className="controls-container">
                        {profileImageUrl && <img src={profileImageUrl} alt="Profile" className="profile-image" />}
                        {Array.isArray(projects) && (
                            <select onChange={handleProjectSelect} value={contextProjectId || ''} className="project-select">
                                <option value="">Select a project</option>
                                {projects.map((project) => (
                                    <option key={project._id} value={project._id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                <div className="menu-buttons">
                    <MenuButton
                        title="First/Then"
                        icon="fa-exchange-alt"
                        onClick={() => navigate(`/firstThenEditor/${contextProjectId}`)}
                    />
                    <MenuButton
                        title="Choices"
                        icon="fa-random"
                        onClick={() => navigate(`/choiceEditor/${contextProjectId}`)}
                    />
                    <MenuButton
                        title="Activity Schedules"
                        icon="fa-calendar"
                        onClick={() => navigate(`/planEditor/${contextProjectId}`)}
                    />
                    <MenuButton
                        title="Social Stories"
                        icon="fa-book"
                        onClick={() => navigate(`/socialstories/${contextProjectId}`)}
                    />
                    <MenuButton
                        title="Reports"
                        icon="fa-chart-bar"
                        onClick={() => navigate(`/reports/${contextProjectId}`)}
                    />
                </div>
                <BottomNavBar projectId={contextProjectId} />
            </div>
        </div>
    );
};

export default Home;
